import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, Loader2, LogIn, Search,
  History, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { canAccessTab, ROLE_LABELS } from './adminAccessMatrix';
import { fmtServerDateTime } from './utils/datetime';

const PAGE_SIZE = 25;

function fmt(iso) {
  if (!iso) return '—';
  return fmtServerDateTime(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * User Troubleshooting Console.
 *
 * Visible to SuperAdmin, SupportExecutive, TechnicalExpert (gated via
 * `canAccessTab('user_console')`). SuperAdmins additionally see the
 * read-only Audit Logs panel — Support and Technical can WRITE entries
 * by triggering impersonations but cannot inspect the history, mirroring
 * a real liability-audit setup.
 */
export function AdminUserConsolePage() {
  const { admin, loading } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(true);

  // Impersonation confirmation dialog
  const [target, setTarget] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Audit Logs panel (SuperAdmin only)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);

  const canViewAudit = canAccessTab(admin, 'user_audit_logs');

  const loadUsers = useCallback(async () => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/impersonation/users', {
        params: { page, page_size: PAGE_SIZE, search },
      });
      setUsers(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load users');
    } finally {
      setFetching(false);
    }
  }, [page, search]);

  useEffect(() => {
    if (admin) loadUsers();
  }, [admin, loadUsers]);

  const loadAudit = useCallback(async (p = 1) => {
    if (!canViewAudit) return;
    setAuditLoading(true);
    try {
      const r = await adminApi.get('/admin/impersonation/audit-logs', {
        params: { page: p, page_size: PAGE_SIZE },
      });
      setAuditLogs(r.data?.items || []);
      setAuditTotal(r.data?.total || 0);
      setAuditPage(p);
    } catch {
      toast.error('Could not load audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [canViewAudit]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (!canAccessTab(admin, 'user_console')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border border-slate-200 rounded-xl p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h2 className="text-base font-semibold text-slate-900 mb-1">Access denied</h2>
          <p className="text-sm text-slate-600 mb-4">
            User Troubleshooting Console is restricted to SuperAdmin, Support Executive, and Technical Expert roles.
          </p>
          <Link to="/admin">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const startImpersonation = async () => {
    if (!target) return;
    const trimmed = (purpose || '').trim();
    if (trimmed.length < 5) {
      toast.error('Please describe the purpose (min 5 characters) for the audit log.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminApi.post(`/admin/impersonation/login-as/${target.id}`, {
        purpose: trimmed,
      });
      const tokenUrl = `${window.location.origin}/auth/impersonate?token=${encodeURIComponent(r.data.access_token)}`;
      // Open the impersonation session in a separate browser tab so the
      // admin's own admin-panel session stays untouched.
      window.open(tokenUrl, '_blank', 'noopener,noreferrer');
      toast.success(`Impersonation session opened for ${target.email}`);
      setTarget(null);
      setPurpose('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start impersonation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-200" data-testid="admin-back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <ShieldCheck className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-lg font-semibold">User Troubleshooting Console</h1>
              <p className="text-xs text-slate-400">
                Acting as: <span className="text-slate-200">{admin.email}</span> · {ROLE_LABELS[admin.role] || admin.role}
              </p>
            </div>
          </div>
          {canViewAudit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setAuditOpen(true); loadAudit(1); }}
              data-testid="open-audit-logs-btn"
              className="bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700"
            >
              <History className="w-4 h-4 mr-1.5" /> Audit Logs
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-100">
          <strong>Every impersonation is logged.</strong> The token issued opens a new tab,
          authenticates as the target user for 15 minutes, and writes an immutable audit entry
          tying your admin ID to the target organisation + purpose you provide.
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, email, or company…"
            className="pl-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
            data-testid="user-console-search"
          />
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">User</th>
                <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                <th className="text-left px-4 py-2.5 font-semibold">Organization</th>
                <th className="text-left px-4 py-2.5 font-semibold">Created</th>
                <th className="text-right px-4 py-2.5 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {fetching && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!fetching && users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No users match.</td></tr>
              )}
              {!fetching && users.map((u) => (
                <tr key={u.id} className="border-t border-slate-800 hover:bg-slate-800/40 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-100">{u.name || '—'}</div>
                    <div className="text-[11px] text-slate-500">ID: {u.id}</div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-200">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-300">
                    {u.organization_name || <span className="text-slate-500 italic">no org</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{fmt(u.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      size="sm"
                      onClick={() => { setTarget(u); setPurpose(''); }}
                      data-testid={`login-as-btn-${u.id}`}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
                    >
                      <LogIn className="w-3.5 h-3.5 mr-1.5" /> Login As
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="border-t border-slate-800 px-4 py-2.5 flex items-center justify-between text-xs text-slate-400">
              <span>Page {page} of {totalPages} · {total} total</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="bg-slate-800 border-slate-700 text-slate-200">Prev</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="bg-slate-800 border-slate-700 text-slate-200">Next</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* "Login As" confirmation + purpose capture */}
      <Dialog open={!!target} onOpenChange={(o) => { if (!o && !submitting) { setTarget(null); setPurpose(''); } }}>
        <DialogContent className="max-w-lg" data-testid="login-as-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-500" /> Confirm impersonation
            </DialogTitle>
            <DialogDescription>
              You're about to authenticate as <strong>{target?.email}</strong>
              {target?.organization_name && <> (org: <strong>{target.organization_name}</strong>)</>}.
              A short-lived (15 min) user session token will be issued and an immutable audit entry will be written.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Purpose (required, audit-logged verbatim)</label>
            <Textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Ticket #4231 — customer reported invoice PDF stuck on 'rendering'."
              rows={3}
              data-testid="impersonation-purpose"
              maxLength={500}
            />
            <div className="text-[11px] text-slate-500">{purpose.length}/500 · min 5 characters</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTarget(null); setPurpose(''); }} disabled={submitting} data-testid="login-as-cancel">
              Cancel
            </Button>
            <Button
              onClick={startImpersonation}
              disabled={submitting || purpose.trim().length < 5}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
              data-testid="login-as-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1.5" />}
              Open impersonation tab
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Logs panel (SuperAdmin only) */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-500" /> User Audit Logs
            </DialogTitle>
            <DialogDescription>
              Immutable record of every impersonation session. SuperAdmin read-only.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Admin</th>
                  <th className="text-left px-3 py-2">Target</th>
                  <th className="text-left px-3 py-2">Purpose</th>
                  <th className="text-left px-3 py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</td></tr>
                )}
                {!auditLoading && auditLogs.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">No audit entries yet.</td></tr>
                )}
                {!auditLoading && auditLogs.map((l) => (
                  <tr key={l.id} className="border-t border-slate-200 align-top" data-testid={`audit-row-${l.id}`}>
                    <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{fmt(l.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{l.admin_email}</div>
                      <div className="text-[10px] text-slate-500">{ROLE_LABELS[l.admin_role] || l.admin_role}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-slate-800">{l.target_email}</div>
                      <div className="text-[10px] text-slate-500">{l.target_organization_name || l.target_organization_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-xs max-w-xs">{l.purpose}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{l.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Page {auditPage} of {Math.ceil(auditTotal / PAGE_SIZE)} · {auditTotal} total</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={auditPage === 1} onClick={() => loadAudit(auditPage - 1)}>Prev</Button>
                <Button variant="outline" size="sm" disabled={auditPage * PAGE_SIZE >= auditTotal} onClick={() => loadAudit(auditPage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminUserConsolePage;
